import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

const GlobalErrorContext = createContext(null);

function normalizeAxiosError(err) {
  if (!err?.isAxiosError && err?.name !== 'AxiosError') {
    return {
      kind: 'unknown',
      title: 'Something went wrong',
      detail: err?.message || 'An unexpected error occurred.',
    };
  }
  if (err.code === 'ECONNABORTED' || err.message?.toLowerCase().includes('timeout')) {
    return {
      kind: 'timeout',
      title: 'Request timed out',
      detail: 'The server took too long to respond. Check your connection and try again.',
    };
  }
  if (!err.response) {
    return {
      kind: 'network',
      title: 'Can’t reach the server',
      detail:
        err.message === 'Network Error'
          ? 'The API may be offline, or your network blocked the request. If you run the backend locally, confirm it is running on the expected port.'
          : err.message || 'No response from the server.',
    };
  }
  const status = err.response.status;
  if (status >= 500) {
    return {
      kind: 'server',
      title: 'Server error',
      detail:
        (typeof err.response.data === 'object' && err.response.data?.detail) ||
        'Something went wrong on the server. Please try again in a moment.',
    };
  }
  if (status === 401) {
    return {
      kind: 'auth',
      title: 'Session issue',
      detail:
        (typeof err.response.data === 'object' && err.response.data?.detail) ||
        'Your session may have expired. Sign in again to continue.',
    };
  }
  if (status === 403) {
    return {
      kind: 'auth',
      title: 'Access denied',
      detail: 'You are not allowed to perform this action.',
    };
  }
  if (status === 404) {
    return {
      kind: 'client',
      title: 'Not found',
      detail:
        (typeof err.response.data === 'object' && err.response.data?.detail) ||
        'The requested resource was not found.',
    };
  }
  return {
    kind: 'client',
    title: 'Request failed',
    detail:
      (typeof err.response.data === 'object' && err.response.data?.detail) ||
      err.message ||
      `Request failed (${status}).`,
  };
}

export function GlobalErrorProvider({ children }) {
  const [errorPayload, setErrorPayload] = useState(null);
  const navigate = useNavigate();

  const clearError = useCallback(() => setErrorPayload(null), []);

  const dismissAndGoHome = useCallback(() => {
    setErrorPayload(null);
    navigate('/dashboard', { replace: true });
  }, [navigate]);

  const reportError = useCallback((err) => {
    setErrorPayload(normalizeAxiosError(err));
  }, []);

  useEffect(() => {
    const onRejection = (event) => {
      const reason = event.reason;
      const isAxios = reason?.isAxiosError || reason?.name === 'AxiosError';

      if (isAxios) {
        const url = String(reason.config?.url || '');
        if (url.includes('token/refresh')) return;
        event.preventDefault();
        setErrorPayload(normalizeAxiosError(reason));
        return;
      }

      if (reason instanceof Error) {
        event.preventDefault();
        setErrorPayload({
          kind: 'unknown',
          title: 'Something went wrong',
          detail: reason.message || 'An unexpected error occurred.',
        });
      }
    };

    window.addEventListener('unhandledrejection', onRejection);
    return () => window.removeEventListener('unhandledrejection', onRejection);
  }, []);

  const value = useMemo(
    () => ({ errorPayload, clearError, dismissAndGoHome, reportError }),
    [errorPayload, clearError, dismissAndGoHome, reportError]
  );

  const goLogin = useCallback(() => {
    clearError();
    navigate('/login');
  }, [clearError, navigate]);

  const retry = useCallback(() => {
    clearError();
    window.location.reload();
  }, [clearError]);

  return (
    <GlobalErrorContext.Provider value={value}>
      {children}
      {errorPayload && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm px-4"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="global-error-title"
          aria-describedby="global-error-desc"
        >
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl shadow-slate-900/20">
            <p
              id="global-error-title"
              className="text-lg font-black text-slate-900 tracking-tight"
              style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
            >
              {errorPayload.title}
            </p>
            <p id="global-error-desc" className="mt-2 text-sm text-slate-600 leading-relaxed">
              {errorPayload.detail}
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={retry}
                className="rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-slate-800 transition-colors"
              >
                Retry
              </button>
              {errorPayload.kind === 'auth' && (
                <button
                  type="button"
                  onClick={goLogin}
                  className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Sign in
                </button>
              )}
              <button
                type="button"
                onClick={dismissAndGoHome}
                className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
              >
                Go to dashboard
              </button>
            </div>
          </div>
        </div>
      )}
    </GlobalErrorContext.Provider>
  );
}

export function useGlobalError() {
  const ctx = useContext(GlobalErrorContext);
  if (!ctx) {
    throw new Error('useGlobalError must be used within GlobalErrorProvider');
  }
  return ctx;
}
